"use client";

import { c as _c } from "react/compiler-runtime";
import { KeyboardEvent, MouseEvent, ReactNode, useCallback, useId } from "react";
import { ChatBubbleLeftRightIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ApiWave } from "@/generated/models/ApiWave";
import { getRandomColorWithSeed, numberWithCommas } from "@/helpers/Helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import WaveItemDropped from "./WaveItemDropped";
import WaveItemFollow from "./WaveItemFollow";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { Tooltip } from "react-tooltip";
const LEVEL_CLASSES: ReadonlyArray<{
  readonly minLevel: number;
  readonly classes: string;
}> = [{
  minLevel: 80,
  classes: "tw-text-[#55B075] tw-ring-[#55B075]"
}, {
  minLevel: 60,
  classes: "tw-text-[#AABE68] tw-ring-[#AABE68]"
}, {
  minLevel: 40,
  classes: "tw-text-[#DAC660] tw-ring-[#DAC660]"
}, {
  minLevel: 20,
  classes: "tw-text-[#DAAC60] tw-ring-[#DAAC60]"
}, {
  minLevel: 0,
  classes: "tw-text-[#DA8C60] tw-ring-[#DA8C60]"
}];
const DEFAULT_LEVEL_CLASS = LEVEL_CLASSES.at(-1)?.classes ?? "";
const CARD_BASE_CLASSES = "tw-@container/wave tw-group tw-rounded-xl tw-ring-1 tw-ring-inset tw-ring-white/10 tw-bg-iron-950 tw-backdrop-blur-sm tw-shadow-sm tw-shadow-black/20 tw-transition-all tw-duration-300 tw-ease-out";
const CARD_INTERACTIVE_CLASSES = "tw-cursor-pointer desktop-hover:hover:tw-shadow-lg desktop-hover:hover:tw-shadow-black/40 desktop-hover:hover:tw-translate-y-[-1px] focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-900 focus-visible:tw-outline-none";
const INTERACTIVE_TAGS = new Set(["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA", "LABEL"]);
const shouldSkipNavigation = (target: HTMLElement | null, root: HTMLElement): boolean => {
  let current: HTMLElement | null = target;
  while (current) {
    if (current === root) {
      break;
    }
    if (current.dataset?.["waveItemInteractive"] === "true") {
      return true;
    }
    if (INTERACTIVE_TAGS.has(current.tagName)) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
};
type CardContainerProps = {
  readonly isInteractive: boolean;
  readonly href?: string | undefined;
  readonly ariaLabel?: string | undefined;
  readonly children: ReactNode;
  readonly onClick?: ((event: MouseEvent<HTMLAnchorElement>) => void) | undefined;
  readonly onKeyDown?: ((event: KeyboardEvent<HTMLAnchorElement>) => void) | undefined;
};
function CardContainer(t0) {
  const $ = _c(13);
  const {
    isInteractive,
    href,
    ariaLabel,
    onClick,
    onKeyDown,
    children
  } = t0;
  const className = `${CARD_BASE_CLASSES} ${isInteractive ? CARD_INTERACTIVE_CLASSES : ""} tw-no-underline`;
  if (isInteractive && href) {
    let t1;
    if ($[0] !== onClick) {
      t1 = onClick ? {
        onClick
      } : {};
      $[0] = onClick;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== ariaLabel || $[3] !== children || $[4] !== className || $[5] !== href || $[6] !== onKeyDown || $[7] !== t1) {
      t2 = <Link href={href} prefetch={false} className={className} aria-label={ariaLabel} {...t1} onKeyDown={onKeyDown}>{children}</Link>;
      $[2] = ariaLabel;
      $[3] = children;
      $[4] = className;
      $[5] = href;
      $[6] = onKeyDown;
      $[7] = t1;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  let t1;
  if ($[9] !== ariaLabel || $[10] !== children || $[11] !== className) {
    t1 = <div className={className} aria-label={ariaLabel}>{children}</div>;
    $[9] = ariaLabel;
    $[10] = children;
    $[11] = className;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  return t1;
}
function getCardLabel(href?: string, label?: string | null) {
  if (!href) {
    return undefined;
  }
  return label ? `View wave ${label}` : "View wave";
}
function resolveLevelClasses(level?: number | null) {
  return LEVEL_CLASSES.find(levelClass => levelClass.minLevel <= (level ?? 0))?.classes ?? DEFAULT_LEVEL_CLASS;
}
export default function WaveItem(t0) {
  const $ = _c(114);
  const {
    wave,
    userPlaceholder,
    titlePlaceholder
  } = t0;
  const router = useRouter();
  const author = wave?.author;
  const authorHref = author?.handle ? `/${author.handle}` : undefined;
  const authorLevel = author?.level ?? 0;
  const tooltipBaseId = useId();
  let t1;
  if ($[0] !== author?.banner1_color || $[1] !== author?.handle || $[2] !== userPlaceholder) {
    t1 = author?.banner1_color ?? getRandomColorWithSeed(author?.handle ?? userPlaceholder ?? "");
    $[0] = author?.banner1_color;
    $[1] = author?.handle;
    $[2] = userPlaceholder;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const banner1 = t1;
  let t2;
  if ($[4] !== author?.banner2_color || $[5] !== author?.handle || $[6] !== userPlaceholder) {
    t2 = author?.banner2_color ?? getRandomColorWithSeed(author?.handle ?? userPlaceholder ?? "");
    $[4] = author?.banner2_color;
    $[5] = author?.handle;
    $[6] = userPlaceholder;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const banner2 = t2;
  const isDirectMessage = wave?.chat?.scope?.group?.is_direct_message ?? false;
  let t3;
  let waveHref;
  if ($[8] !== isDirectMessage || $[9] !== wave) {
    waveHref = wave ? getWaveRoute({
      waveId: wave.id,
      isDirectMessage,
      isApp: false
    }) : undefined;
    const labelValue = wave?.name ?? wave?.id;
    t3 = getCardLabel(waveHref, labelValue);
    $[8] = isDirectMessage;
    $[9] = wave;
    $[10] = t3;
    $[11] = waveHref;
  } else {
    t3 = $[10];
    waveHref = $[11];
  }
  const cardLabel = t3;
  const isInteractive = Boolean(waveHref);
  const followersTooltipId = wave ? `${tooltipBaseId}-followers` : undefined;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <UserGroupIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-400" />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = wave?.metrics.subscribers_count ?? 0;
  let t6;
  if ($[13] !== t5) {
    t6 = numberWithCommas(t5);
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <span className="tw-font-medium">{t6}</span>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="tw-text-iron-400 xl:tw-hidden">Joined</span>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7) {
    t9 = <>{t4}{t7}{t8}</>;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const followersContent = t9;
  let t10;
  if ($[20] !== author) {
    t10 = author?.pfp ? <img className="tw-h-full tw-w-full tw-rounded-md tw-object-cover tw-bg-iron-800 tw-ring-1 tw-ring-white/10 desktop-hover:group-hover/author:tw-ring-white/30 desktop-hover:group-hover/author:tw-ring-offset-1 desktop-hover:group-hover/author:tw-ring-offset-iron-950 tw-transition tw-duration-300 tw-ease-out" src={getScaledImageUri(author.pfp, ImageScale.W_200_H_200)} alt={author?.handle ? `${author.handle} avatar` : "Author avatar"} loading="lazy" decoding="async" /> : <div className="tw-h-full tw-w-full tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-white/10" />;
    $[20] = author;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const authorAvatar = t10;
  const t11 = author?.level;
  let t12;
  if ($[22] !== t11) {
    t12 = resolveLevelClasses(t11);
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = `${t12} tw-border-none tw-inline-flex tw-items-center tw-rounded-xl tw-bg-transparent tw-px-2 tw-py-1 tw-font-semibold tw-ring-2 tw-ring-inset tw-text-[0.625rem] tw-leading-3 tw-whitespace-nowrap`;
  let t14;
  if ($[24] !== authorLevel || $[25] !== t13) {
    t14 = <div className={t13}>Level {authorLevel}</div>;
    $[24] = authorLevel;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const authorLevelBadge = t14;
  let t15;
  if ($[27] !== authorHref || $[28] !== router) {
    t15 = event => {
      if (!authorHref) {
        return;
      }
      if (event.metaKey || event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();
        window.open(authorHref, "_blank", "noopener,noreferrer");
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      router.push(authorHref);
    };
    $[27] = authorHref;
    $[28] = router;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const handleAuthorClick = t15;
  let t16;
  if ($[30] !== authorHref) {
    t16 = event_0 => {
      if (!authorHref || event_0.button !== 1) {
        return;
      }
      event_0.preventDefault();
      event_0.stopPropagation();
      window.open(authorHref, "_blank", "noopener,noreferrer");
    };
    $[30] = authorHref;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const handleAuthorAuxClick = t16;
  let authorSection;
  if (!wave) {
    let t17;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <div className="tw-h-8 tw-w-8 tw-relative tw-flex-shrink-0 tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-iron-700 tw-mr-3" />;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] !== userPlaceholder) {
      t18 = <span className="tw-group/author tw-flex tw-items-center tw-transition-opacity hover:tw-opacity-80">{t17}<span className="tw-text-sm tw-font-bold tw-text-white tw-truncate tw-max-w-[120px]">{userPlaceholder}</span></span>;
      $[33] = userPlaceholder;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    authorSection = t18;
  } else {
    if (authorHref) {
      const t17 = author?.handle ? `View @${author.handle}` : "View author profile";
      let t18;
      if ($[35] !== authorAvatar) {
        t18 = <div className="tw-h-8 tw-w-8 tw-relative tw-flex-shrink-0 tw-mr-3">{authorAvatar}</div>;
        $[35] = authorAvatar;
        $[36] = t18;
      } else {
        t18 = $[36];
      }
      const t19 = author?.handle ?? userPlaceholder;
      let t20;
      if ($[37] !== t19) {
        t20 = <span className="tw-text-sm tw-font-bold tw-text-white tw-truncate tw-min-w-0">{t19}</span>;
        $[37] = t19;
        $[38] = t20;
      } else {
        t20 = $[38];
      }
      let t21;
      if ($[39] !== authorLevelBadge) {
        t21 = <div className="tw-ml-1.5 tw-flex tw-items-center">{authorLevelBadge}</div>;
        $[39] = authorLevelBadge;
        $[40] = t21;
      } else {
        t21 = $[40];
      }
      let t22;
      if ($[41] !== handleAuthorAuxClick || $[42] !== handleAuthorClick || $[43] !== t17 || $[44] !== t18 || $[45] !== t20 || $[46] !== t21) {
        t22 = <button type="button" data-wave-item-interactive="true" onClick={handleAuthorClick} onAuxClick={handleAuthorAuxClick} className="tw-group/author tw-flex tw-items-center tw-transition-opacity hover:tw-opacity-80 tw-cursor-pointer tw-bg-transparent tw-border-none tw-p-0 tw-text-left tw-relative tw-z-10 tw-min-w-0 tw-w-fit" aria-label={t17}>{t18}{t20}{t21}</button>;
        $[41] = handleAuthorAuxClick;
        $[42] = handleAuthorClick;
        $[43] = t17;
        $[44] = t18;
        $[45] = t20;
        $[46] = t21;
        $[47] = t22;
      } else {
        t22 = $[47];
      }
      authorSection = t22;
    } else {
      let t17;
      if ($[48] !== authorAvatar) {
        t17 = <div className="tw-h-8 tw-w-8 tw-relative tw-flex-shrink-0 tw-mr-3">{authorAvatar}</div>;
        $[48] = authorAvatar;
        $[49] = t17;
      } else {
        t17 = $[49];
      }
      const t18 = author?.handle ?? userPlaceholder;
      let t19;
      if ($[50] !== t18) {
        t19 = <span className="tw-text-sm tw-font-bold tw-text-white tw-truncate tw-min-w-0">{t18}</span>;
        $[50] = t18;
        $[51] = t19;
      } else {
        t19 = $[51];
      }
      let t20;
      if ($[52] !== authorLevelBadge) {
        t20 = <div className="tw-ml-1.5 tw-flex tw-items-center">{authorLevelBadge}</div>;
        $[52] = authorLevelBadge;
        $[53] = t20;
      } else {
        t20 = $[53];
      }
      let t21;
      if ($[54] !== t17 || $[55] !== t19 || $[56] !== t20) {
        t21 = <div className="tw-group/author tw-flex tw-items-center tw-transition-opacity hover:tw-opacity-80 tw-min-w-0">{t17}{t19}{t20}</div>;
        $[54] = t17;
        $[55] = t19;
        $[56] = t20;
        $[57] = t21;
      } else {
        t21 = $[57];
      }
      authorSection = t21;
    }
  }
  let t17;
  if ($[58] !== router || $[59] !== waveHref) {
    t17 = event_1 => {
      if (!waveHref) {
        return;
      }
      const target = event_1.target as HTMLElement | null;
      if (shouldSkipNavigation(target, event_1.currentTarget)) {
        event_1.preventDefault();
        event_1.stopPropagation();
        return;
      }
      if (event_1.button === 1 || event_1.metaKey || event_1.ctrlKey) {
        return;
      }
      if (!event_1.defaultPrevented) {
        event_1.preventDefault();
        router.push(waveHref);
      }
    };
    $[58] = router;
    $[59] = waveHref;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  const handleCardClick = t17;
  let t18;
  if ($[61] !== router || $[62] !== waveHref) {
    t18 = event_2 => {
      if (!waveHref || event_2.target !== event_2.currentTarget) {
        return;
      }
      if (event_2.key === "Enter" || event_2.key === " " || event_2.key === "Space") {
        event_2.preventDefault();
        router.push(waveHref);
      }
    };
    $[61] = router;
    $[62] = waveHref;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  const handleCardKeyDown = t18;
  const t19 = `linear-gradient(45deg, ${banner1} 0%, ${banner2} 100%)`;
  const t20 = wave?.picture ? 0.35 : 1;
  let t21;
  if ($[64] !== t19 || $[65] !== t20) {
    t21 = <div className="tw-absolute tw-inset-0 tw-rounded-xl" style={{
      background: t19,
      opacity: t20
    }} />;
    $[64] = t19;
    $[65] = t20;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== wave) {
    t22 = wave?.picture && <img src={getScaledImageUri(wave.picture, ImageScale.AUTOx450)} alt={wave?.name ? `Wave ${wave.name}` : "Wave picture"} loading="lazy" decoding="async" className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-transition-transform tw-duration-500 tw-will-change-transform desktop-hover:group-hover:tw-scale-[1.015]" />;
    $[67] = wave;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/90 tw-via-black/40 tw-to-transparent" />;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  const t24 = wave?.name ?? titlePlaceholder;
  let t25;
  if ($[70] !== t24) {
    t25 = <div className="tw-flex tw-min-w-0 tw-items-end tw-px-4 tw-pb-5"><div className="tw-min-w-0"><span className="tw-text-base sm:tw-text-lg tw-font-bold tw-text-white tw-leading-tight tw-drop-shadow-lg tw-line-clamp-2">{t24}</span></div></div>;
    $[70] = t24;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="tw-hidden sm:tw-block" />;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== t25) {
    t27 = <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-flex tw-items-end tw-justify-between tw-gap-3">{t25}{t26}</div>;
    $[73] = t25;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== t21 || $[76] !== t22 || $[77] !== t27) {
    t28 = <div className="tw-relative tw-overflow-hidden tw-rounded-t-xl tw-aspect-[16/8.5] sm:tw-aspect-[16/9] tw-border tw-border-solid tw-border-b-0 tw-border-white/[0.003]">{t21}{t22}{t23}{t27}</div>;
    $[75] = t21;
    $[76] = t22;
    $[77] = t27;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  let t29;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-iron-400"><ChatBubbleLeftRightIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-400" /><span className="tw-font-medium">Chat</span></div>;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] !== followersContent || $[81] !== followersTooltipId || $[82] !== waveHref) {
    t30 = <div className="tw-flex tw-items-center tw-gap-4 tw-flex-shrink-0">{t29}{waveHref && followersTooltipId ? <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-no-underline tw-relative tw-z-20 tw-pointer-events-auto" data-tooltip-id={followersTooltipId}>{followersContent}</span> : <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs">{followersContent}</div>}</div>;
    $[80] = followersContent;
    $[81] = followersTooltipId;
    $[82] = waveHref;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  let t31;
  if ($[84] !== followersTooltipId || $[85] !== wave) {
    t31 = wave && followersTooltipId && <Tooltip id={followersTooltipId} place="top" positionStrategy="fixed" style={{
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px",
      zIndex: 50
    }}><span className="tw-text-xs">Joined</span></Tooltip>;
    $[84] = followersTooltipId;
    $[85] = wave;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== authorSection || $[88] !== t30 || $[89] !== t31) {
    t32 = <div className="tw-flex tw-flex-col tw-gap-y-4 @[380px]/wave:tw-flex-row @[380px]/wave:tw-items-center @[380px]/wave:tw-justify-between @[380px]/wave:tw-gap-2">{authorSection}{t30}{t31}</div>;
    $[87] = authorSection;
    $[88] = t30;
    $[89] = t31;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="tw-w-full tw-h-px tw-bg-white/5 tw-my-1" />;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] !== wave) {
    t34 = wave && <div className="tw-overflow-hidden tw-min-w-0"><WaveItemDropped wave={wave} /></div>;
    $[92] = wave;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  let t35;
  if ($[94] !== t34) {
    t35 = <div className="tw-min-w-0 tw-flex-1">{t34}</div>;
    $[94] = t34;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] !== wave) {
    t36 = wave && <div data-wave-item-interactive="true" className="tw-relative tw-z-20 tw-pointer-events-auto"><WaveItemFollow wave={wave} /></div>;
    $[96] = wave;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  let t37;
  if ($[98] !== t36) {
    t37 = <div className="tw-flex tw-items-center">{t36}</div>;
    $[98] = t36;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  let t38;
  if ($[100] !== t35 || $[101] !== t37) {
    t38 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">{t35}{t37}</div>;
    $[100] = t35;
    $[101] = t37;
    $[102] = t38;
  } else {
    t38 = $[102];
  }
  let t39;
  if ($[103] !== t32 || $[104] !== t38) {
    t39 = <div className="tw-p-4 tw-gap-3 tw-flex tw-flex-col">{t32}{t33}{t38}</div>;
    $[103] = t32;
    $[104] = t38;
    $[105] = t39;
  } else {
    t39 = $[105];
  }
  let t40;
  if ($[106] !== cardLabel || $[107] !== handleCardClick || $[108] !== handleCardKeyDown || $[109] !== isInteractive || $[110] !== t28 || $[111] !== t39 || $[112] !== waveHref) {
    t40 = <CardContainer isInteractive={isInteractive} href={waveHref} ariaLabel={cardLabel} onClick={handleCardClick} onKeyDown={handleCardKeyDown}>{t28}{t39}</CardContainer>;
    $[106] = cardLabel;
    $[107] = handleCardClick;
    $[108] = handleCardKeyDown;
    $[109] = isInteractive;
    $[110] = t28;
    $[111] = t39;
    $[112] = waveHref;
    $[113] = t40;
  } else {
    t40 = $[113];
  }
  return t40;
}